import React, { Fragment, useEffect, useRef, useState } from "react";
import { Card, CardBody, Col, Container, FormGroup, Input, Row, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Select from "react-select";
import moment from "moment";
import { DateRangePicker } from "rsuite";
import DateRangePickerPrimary from "../../../share-components/ui/DateRangePickerPrimary";
import Breadcrumb from "../../../layout/breadcrumb";
import withReducer from "../../../store/withReducer";
import inventoryReducer from "../store";
import {
    fetchInventoryHistoryDetails,
    exportInventoryHistoryDetails,
    setInventoryHistoryDetailsFilters,
    setInventoryHistoryDetailsPage,
    setInventoryHistoryDetailsPerPage,
} from "../store/inventoryHistoryDetailsSlice";
import CommonDataTable from "../../../share-components/table/commonDataTable";

const { afterToday } = DateRangePicker;

const typeOptions = [
    { value: 'sale', label: 'Sale' },
    { value: 'return', label: 'Return' },
    { value: 'restock', label: 'Restock' },
    { value: 'manual_adjustment', label: 'Manual Adjustment' },
    { value: 'purchase_received', label: 'Purchase Received' },
];

const dataMenu = [
    {
        type: 0,
        text: 'Inventory History Details',
    }
];

const customStyles = {
    control: base => ({
        ...base,
        minHeight: 50,
        border: 'none',
    })
};

const InventoryHistoryDetails = () => {
    const dispatch = useDispatch();
    const state = useSelector(({ inventory }) => inventory.inventoryHistoryDetails);

    const [sku, setSku] = useState('');
    const [type, setType] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);
    const [exportBtnDisabled, setExportBtnDisabled] = useState(false);

    const getAllInventoryHistoryData = () => {
        dispatch(fetchInventoryHistoryDetails({
            sku: state.filters.sku || null,
            type: state.filters.type || null,
            from: state.filters.from || null,
            to: state.filters.to || null,
            product_variant_id: state.filters.product_variant_id || null,
            page: state.pagination.page,
            per_page: state.pagination.per_page,
        }));
    };

    useEffect(() => {
        getAllInventoryHistoryData();
        // eslint-disable-next-line
    }, [dispatch, state.filters, state.pagination.page, state.pagination.per_page]);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const qsSku = queryParams.get('sku');
        const qsVariantId = queryParams.get('product_variant_id');
        if (qsSku || qsVariantId) {
            setSku(qsSku || '');
            dispatch(setInventoryHistoryDetailsFilters({
                sku: qsSku || '',
                type: '',
                from: null,
                to: null,
                product_variant_id: qsVariantId || null,
            }));
            dispatch(setInventoryHistoryDetailsPage(1));
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (state.error) {
            toast.error(state.error, {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    }, [state.error]);

    // WHY: filters apply live as inputs change (no Apply/Reset buttons) — this builds the
    // merged filter payload from current input state plus whichever field just changed,
    // clearing product_variant_id since the visible filter bar becomes authoritative once
    // the user touches it directly (matches the earlier Apply-button behavior).
    const dispatchFilters = (overrides = {}) => {
        const [startDate, endDate] = Array.isArray(dateRange) ? dateRange : [null, null];
        dispatch(setInventoryHistoryDetailsFilters({
            sku: sku || '',
            type: type?.value || '',
            from: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
            to: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
            product_variant_id: null,
            ...overrides,
        }));
        dispatch(setInventoryHistoryDetailsPage(1));
    };

    const skuDebounceRef = useRef(null);
    useEffect(() => {
        return () => {
            if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current);
        };
    }, []);
    const onSkuChange = (value) => {
        setSku(value);
        if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current);
        skuDebounceRef.current = setTimeout(() => {
            dispatchFilters({ sku: value || '' });
        }, 400);
    };

    const onTypeChange = (selected) => {
        setType(selected);
        dispatchFilters({ type: selected?.value || '' });
    };

    const onDateRangeChange = (date) => {
        const [startDate, endDate] = Array.isArray(date) ? date : [null, null];
        setDateRange(date || [null, null]);
        dispatchFilters({
            from: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
            to: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
        });
    };

    const onPageChange = (page) => {
        dispatch(setInventoryHistoryDetailsPage(page));
    };

    const onPerPageChange = (newPerPage) => {
        dispatch(setInventoryHistoryDetailsPerPage(newPerPage));
    };

    const exportFile = () => {
        if (exportBtnDisabled) return;
        setExportBtnDisabled(true);

        dispatch(exportInventoryHistoryDetails({
            sku: state.filters.sku || null,
            type: state.filters.type || null,
            from: state.filters.from || null,
            to: state.filters.to || null,
            product_variant_id: state.filters.product_variant_id || null,
        })).then((res) => {
            const link = document.createElement("a");
            link.href = res.payload;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
            setExportBtnDisabled(false);
        });
    };

    const tableColumns = [
        {
            name: "Date",
            selector: (row) => row.date,
            sortable: false,
        },
        {
            name: "Type",
            selector: (row) => row.type,
            sortable: false,
        },
        {
            name: "Reason",
            selector: (row) => row.reason || '-',
            sortable: false,
        },
        {
            name: "Created By",
            selector: (row) => row.created_by_name,
            sortable: false,
        },
        {
            name: "Qty",
            selector: (row) => (
                <span style={{ color: row.qty > 0 ? '#62C13B' : (row.qty < 0 ? '#DD3C4C' : undefined), fontWeight: 500 }}>
                    {row.qty > 0 ? `+${row.qty}` : row.qty}
                </span>
            ),
            sortable: false,
            right: true,
        },
        {
            name: "Available Qty",
            selector: (row) => row.available_qty,
            sortable: false,
            right: true,
        },
        {
            name: "SKU",
            selector: (row) => row.sku,
            sortable: false,
        },
        {
            name: "Product",
            selector: (row) => row.product_name || '-',
            sortable: false,
        },
    ];

    return (
        <Fragment>
            <Breadcrumb title="Inventory History Details" data={dataMenu} />
            <Container fluid={true} className="product-wrapper">
                <div className="product-grid">
                    <div className="feature-products">
                        <Card>
                            <Row style={{ justifyContent: "space-between" }} className="align-items-center p-3">
                                <Col md="3">
                                    <FormGroup className="m-0">
                                        <DateRangePickerPrimary
                                            dateRange={dateRange}
                                            dateRangeChange={onDateRangeChange}
                                            size="md"
                                            placeholder="Select start and end date"
                                            cleanable={true}
                                            disabledDate={afterToday()}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <Select
                                        className="select2-filter"
                                        isClearable
                                        placeholder="Select Type"
                                        styles={customStyles}
                                        options={typeOptions}
                                        value={type}
                                        onChange={onTypeChange}
                                        components={{ IndicatorSeparator: () => null }}
                                    />
                                </Col>
                                <Col md="3">
                                    <FormGroup className="m-0">
                                        <Input
                                            className="form-control"
                                            type="text"
                                            placeholder="Search SKU"
                                            value={sku}
                                            onChange={(e) => onSkuChange(e.target.value)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3" className="text-right">
                                    <Button
                                        type="button"
                                        color="primary"
                                        onClick={exportFile}
                                        disabled={exportBtnDisabled}
                                    >
                                        <i className={exportBtnDisabled ? "fa fa-spinner fa-spin mr-1" : "fa fa-download mr-1"}></i>
                                        Export
                                    </Button>
                                </Col>
                            </Row>
                        </Card>

                        <Row>
                            <Col sm="12">
                                <Card>
                                    {state.loading && (
                                        <div className="card-loading">
                                            <i className="fa fa-spinner fa-lg spinner" />
                                        </div>
                                    )}
                                    <CardBody>
                                        <div className="table-responsive product-table">
                                            <CommonDataTable
                                                headerColumns={tableColumns}
                                                gridData={state.data}
                                                noAction
                                                progressPending={state.loading}
                                                paginationServer
                                                paginationTotalRows={state.pagination.total}
                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                paginationDefaultPage={state.pagination.page}
                                                paginationPerPage={state.pagination.per_page}
                                                onChangePage={onPageChange}
                                                onChangeRowsPerPage={onPerPageChange}
                                                noDataComponent={<div className="p-3">No stock movements found for the selected filters.</div>}
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                </div>
            </Container>
        </Fragment>
    );
};

export default withReducer([{ inventory: inventoryReducer }])(InventoryHistoryDetails);
